import { useEffect, useState, useCallback } from "react";
import { Bell, AlertTriangle, Info, TrendingDown, PartyPopper, X, CheckCheck } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";
import { Button } from "../ui/button";
import api from "../../lib/api";

const ICON = {
  bill: AlertTriangle,
  money: TrendingDown,
  budget: AlertTriangle,
  income: Info,
  savings: PartyPopper,
};

const SEV = {
  danger: { dot: "bg-red-500", tint: "bg-red-50", icon: "text-red-600" },
  warning: { dot: "bg-amber-500", tint: "bg-amber-50", icon: "text-amber-600" },
  info: { dot: "bg-sky-500", tint: "bg-sky-50", icon: "text-sky-600" },
  success: { dot: "bg-emerald-500", tint: "bg-emerald-50", icon: "text-emerald-600" },
};

export default function NotificationCenter({ month, refreshKey }) {
  const [notes, setNotes] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/notifications?month=${month}`);
      setNotes(data.notifications);
      setUnread(data.unread);
    } catch { /* ignore */ }
  }, [month]);

  useEffect(() => { load(); }, [load, refreshKey]);

  const markAll = async () => {
    const ids = notes.map((n) => n.id);
    if (!ids.length) return;
    await api.post("/notifications/mark-read", { ids });
    setNotes((prev) => prev.map((n) => ({ ...n, read: true })));
    setUnread(0);
  };

  const dismiss = async (id) => {
    await api.post("/notifications/dismiss", { id });
    setNotes((prev) => prev.filter((n) => n.id !== id));
    setUnread((u) => Math.max(0, u - (notes.find((n) => n.id === id && !n.read) ? 1 : 0)));
  };

  const onOpen = (v) => {
    setOpen(v);
    if (v && unread > 0) markAll();
  };

  return (
    <Popover open={open} onOpenChange={onOpen}>
      <PopoverTrigger asChild>
        <button data-testid="notification-bell" className="relative w-9 h-9 rounded-lg border border-slate-200 bg-white flex items-center justify-center hover:bg-slate-50 transition-colors">
          <Bell className="w-4 h-4 text-slate-600" />
          {unread > 0 && (
            <span data-testid="notification-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-orange-600 text-white text-[10px] font-bold flex items-center justify-center">
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[340px] p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <p className="font-display font-semibold text-slate-900 text-sm">Notifications</p>
          {notes.length > 0 && (
            <button onClick={markAll} data-testid="notification-mark-all" className="text-xs text-orange-600 hover:text-orange-700 flex items-center gap-1">
              <CheckCheck className="w-3.5 h-3.5" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[380px] overflow-y-auto">
          {notes.length === 0 && (
            <div className="py-10 text-center text-sm text-slate-400">
              <Bell className="w-6 h-6 mx-auto mb-2 text-slate-300" />
              You're all caught up.
            </div>
          )}
          {notes.map((n) => {
            const sev = SEV[n.severity] || SEV.info;
            const Icon = ICON[n.type] || Info;
            return (
              <div key={n.id} data-testid={`notification-item-${n.id}`} className={`flex gap-3 px-4 py-3 border-b border-slate-50 relative ${n.read ? "" : sev.tint}`}>
                <div className={`w-8 h-8 rounded-lg bg-white border border-slate-100 flex items-center justify-center shrink-0 ${sev.icon}`}>
                  <Icon className="w-4 h-4" />
                </div>
                <div className="flex-1 min-w-0 pr-4">
                  <div className="flex items-center gap-2">
                    {!n.read && <span className={`w-1.5 h-1.5 rounded-full ${sev.dot}`} />}
                    <p className="text-sm font-medium text-slate-800 truncate">{n.title}</p>
                  </div>
                  <p className="text-xs text-slate-500 mt-0.5 leading-snug">{n.message}</p>
                </div>
                <button onClick={() => dismiss(n.id)} data-testid={`notification-dismiss-${n.id}`} className="absolute top-2 right-2 text-slate-300 hover:text-slate-600">
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
